import React, { useState, useEffect } from 'react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { DollarSign, TrendingUp, Users, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Subscription {
  subscription_id: string;
  user_id: string;
  nome: string;
  email: string;
  plan_type: 'monthly' | 'annual';
  price_id: string;
  status: string;
  current_period_start: string;
  current_period_end: string;
}

const SubscriptionsPage: React.FC = () => {
  const { supabase } = useSupabase();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterPlan, setFilterPlan] = useState('all');

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);

      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        console.error('No active session');
        setSubscriptions([]);
        setLoading(false);
        return;
      }

      // Call admin edge function to fetch subscriptions
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-get-subscriptions`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error fetching subscriptions:', errorData);
        throw new Error(errorData.error || 'Failed to fetch subscriptions');
      }

      const result = await response.json();

      if (result.success && result.subscriptions) {
        setSubscriptions(result.subscriptions);
      } else {
        setSubscriptions([]);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredSubscriptions = subscriptions.filter((sub) => {
    if (filterPlan === 'all') return true;
    return sub.plan_type === filterPlan;
  });

  // Calcular MRR baseado nas assinaturas ativas
  const activeSubscriptions = subscriptions.filter(s => s.status === 'active' || s.status === 'trialing');
  const monthlyRevenue = activeSubscriptions.filter(s => s.plan_type === 'monthly').length * 27;
  const annualRevenue = activeSubscriptions.filter(s => s.plan_type === 'annual').length * 270;
  const totalMRR = monthlyRevenue + (annualRevenue / 12);

  // Traduzir status do Stripe para português
  const getStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
      active: 'Ativo',
      trialing: 'Trial',
      past_due: 'Vencido',
      canceled: 'Cancelado',
      unpaid: 'Não Pago',
      incomplete: 'Incompleto',
    };
    return statusMap[status] || status;
  };

  // Definir cor do status
  const getStatusColor = (status: string): string => {
    const colorMap: Record<string, string> = {
      active: 'bg-green-500',
      trialing: 'bg-blue-500',
      past_due: 'bg-orange-500',
      canceled: 'bg-red-500',
      unpaid: 'bg-red-600',
      incomplete: 'bg-gray-500',
    };
    return colorMap[status] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-white">Gerenciamento de Assinaturas</h1>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">MRR Total</p>
              <p className="text-3xl font-bold text-green-400 mt-2">
                R$ {totalMRR.toFixed(2)}
              </p>
            </div>
            <DollarSign className="w-10 h-10 text-green-400" />
          </div>
        </div>

        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Assinaturas Ativas</p>
              <p className="text-3xl font-bold text-blue-400 mt-2">
                {activeSubscriptions.length}
              </p>
            </div>
            <Users className="w-10 h-10 text-blue-400" />
          </div>
        </div>

        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Planos Mensais</p>
              <p className="text-3xl font-bold text-purple-400 mt-2">
                {subscriptions.filter(s => s.plan_type === 'monthly').length}
              </p>
            </div>
            <Calendar className="w-10 h-10 text-purple-400" />
          </div>
        </div>

        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Planos Anuais</p>
              <p className="text-3xl font-bold text-amber-400 mt-2">
                {subscriptions.filter(s => s.plan_type === 'annual').length}
              </p>
            </div>
            <TrendingUp className="w-10 h-10 text-amber-400" />
          </div>
        </div>
      </div>

      <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Todas as Assinaturas</h2>
          <select
            value={filterPlan}
            onChange={(e) => setFilterPlan(e.target.value)}
            className="px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            <option value="all">Todos os Planos</option>
            <option value="monthly">Mensal</option>
            <option value="annual">Anual</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-3 px-4 text-slate-300 font-medium">Usuário</th>
                <th className="text-left py-3 px-4 text-slate-300 font-medium">Email</th>
                <th className="text-left py-3 px-4 text-slate-300 font-medium">Plano</th>
                <th className="text-left py-3 px-4 text-slate-300 font-medium">Status</th>
                <th className="text-left py-3 px-4 text-slate-300 font-medium">Início</th>
                <th className="text-left py-3 px-4 text-slate-300 font-medium">Renovação</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubscriptions.map((sub) => (
                <tr key={sub.subscription_id} className="border-b border-slate-700 hover:bg-slate-700/50">
                  <td className="py-3 px-4 text-white">{sub.nome}</td>
                  <td className="py-3 px-4 text-slate-300">{sub.email}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs text-white ${
                      sub.plan_type === 'monthly' ? 'bg-blue-500' : 'bg-purple-500'
                    }`}>
                      {sub.plan_type === 'monthly' ? 'Mensal' : 'Anual'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs text-white ${getStatusColor(sub.status)}`}>
                      {getStatusLabel(sub.status)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-slate-300">
                    {sub.current_period_start
                      ? format(new Date(sub.current_period_start), 'dd/MM/yyyy', { locale: ptBR })
                      : '-'}
                  </td>
                  <td className="py-3 px-4 text-slate-300">
                    {sub.current_period_end
                      ? format(new Date(sub.current_period_end), 'dd/MM/yyyy', { locale: ptBR })
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredSubscriptions.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              Nenhuma assinatura encontrada
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionsPage;
